import { Playbook } from '@driftt/api-types'
import { access } from '@driftt/traffic-control'

import { isFastLanePlaybook } from './playbooks'

export const {
  hasScopes,
  hasAnyScope,
  hasAllGates,
  hasGates,
  hasAnyRole,
  isAdmin,
  isAgent,
  isUser,
  hasFeatures,
  isBot,
} = access

const isPlaybookEditorUser = () => hasAnyRole('Playbooks')
export const userHasAllPlaybookAccess = () =>
  isAdmin() || isPlaybookEditorUser()

export const hasExportBotCopy = () =>
  hasGates('enterprise_04_2018') || hasGates('export_bot_copy') || isBot()

export const getHasPlaybookExperiments = () =>
  hasGates('has_playbook_variation_testing')

export const userHasChatPlaybooksReadOnly = () =>
  hasFeatures({
    featureName: 'chat_playbooks_read_only',
    accessLevel: 'READ',
  })

export const userHasChatPlaybooksWrite = () =>
  hasFeatures({
    featureName: 'chat_playbooks',
    accessLevel: 'WRITE',
  })

export const userHasChatPlaybooks = () =>
  userHasAllPlaybookAccess() ||
  userHasChatPlaybooksReadOnly() ||
  userHasChatPlaybooksWrite()

export const userHasChatPlaybookReports = () =>
  userHasAllPlaybookAccess() ||
  hasFeatures({ featureName: 'chat_playbook_reports', accessLevel: 'READ' })
export const userHasEmailPlaybooks = () => hasGates('send_email_skill')
export const userHasMeetingsPlaybooks = () =>
  hasGates('meeting_booking_playbook')
export const userHasContentRecommendations = () =>
  hasGates('content_recommendations')
export const userHasSearchAppExperience = () =>
  hasGates('search_app_experience')
export const userHasMeetingBookingAppExperience = () =>
  hasGates('meeting_booking_experience')
export const scWindDown = () => hasGates('sc_wind_down_dev')
export const userHasWidgetSettings = () =>
  isAdmin() ||
  hasFeatures({ featureName: 'widget_settings', accessLevel: 'WRITE' })

const hasFastlanePlaybooksGate = () =>
  userHasAllPlaybookAccess() && hasGates('has_form_submission_fast_lane')

export const userHasFastlanePlaybooks = () =>
  hasFastlanePlaybooksGate() ||
  hasFeatures({
    featureName: 'fastlane_playbooks',
    accessLevel: 'WRITE',
  })

export const userHasFastlanePlaybooksReports = () =>
  hasFastlanePlaybooksGate() ||
  hasFeatures({
    featureName: 'fastlane_playbook_reports',
    accessLevel: 'READ',
  })

export const userHasPlaybooksEditorAccess = () =>
  userHasChatPlaybooks() || userHasFastlanePlaybooks()

export const userHasPlaybookReports = () =>
  userHasChatPlaybookReports() || userHasFastlanePlaybooksReports()

export const userHasReusableBlocksV2 = () => hasGates('blocks_v2')

export const userHasGlobalResponses = () => hasGates('global_bot_responses')

export const userHasAudienceEstimator = () =>
  userHasAllPlaybookAccess() ||
  hasFeatures({ featureName: 'show_audience_estimator', accessLevel: 'READ' })

export const userHasAIPlaybooks = () =>
  (userHasAllPlaybookAccess() && hasGates('has_ai_playbooks')) ||
  hasFeatures({ featureName: 'has_ai_playbooks', accessLevel: 'READ' })

export const userHasTopicTraining = () =>
  userHasAIPlaybooks() && hasGates('has_ai_topic_training')

export const userHasTopicGrouping = () => userHasTopicTraining()

export const userHasAnyPlaybookAccess = () =>
  userHasChatPlaybooks() ||
  userHasFastlanePlaybooks() ||
  userHasEmailPlaybooks()

export const getWorkspacesWithChatPlaybooksWrite = () =>
  access.getWorkspacesWithPermission('chat_playbooks', 'WRITE')

export const getWorkspacesWithFastlanePlaybooksWrite = () =>
  access.getWorkspacesWithPermission('fastlane_playbooks', 'WRITE')

export const getWorkspacesWithWritablePlaybooks = (
  playbook: Pick<Playbook, 'reportType'>
) =>
  access.getWorkspacesWithPermission(
    `${isFastLanePlaybook(playbook) ? 'fastlane' : 'chat'}_playbooks`,
    'WRITE'
  )

export const getWorkspacesWithChatPlaybookRead = () =>
  access.getWorkspacesWithPermission('chat_playbooks', 'READ')

export const getWorkspacesWithFastlanePlaybookRead = () =>
  access.getWorkspacesWithPermission('fastlane_playbooks', 'READ')

export const getWorkspacesWithReadablePlaybooks = (
  playbook: Pick<Playbook, 'reportType'>
) =>
  access.getWorkspacesWithPermission(
    `${isFastLanePlaybook(playbook) ? 'fastlane' : 'chat'}_playbooks`,
    'READ'
  )

export const isNotFreeTierOrg = () =>
  hasGates('standard_trial') ||
  hasGates('premium_team') ||
  hasGates('premium_drift') ||
  hasGates('premium_enterprise')

export const getIsBotUser = () => isBot()

export const userHasPlaybookPriorityAccess = () =>
  hasFeatures({ featureName: 'chat_playbook_priority', accessLevel: 'READ' })

export const orgHasPlaybookPriorityFeature = () =>
  hasGates('chat_playbook_priority')

export const orgHasWorkspaceAccess = () => hasGates('workspace_settings')

export const hasPlaybookContentViewMode = () =>
  hasGates('playbook_content_view')

export const userHasContentVariables = () => hasGates('has_content_variables')

export const userHasBionicChatbots = () =>
  hasGates('has_bionic') ||
  (hasGates('has_ai_answers_skill') && hasGates('has_ai_resource_redirect'))

export const userHasLeadQual = () =>
  isAdmin() && userHasBionicChatbots() && hasGates('ai_leadqual_skill_node')

export const userHasChatExperience = () => hasGates('agents_prompt')
